import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import Swal from 'sweetalert2';
import MultiImagePicker from '../../components/MultiImagePicker';
import ImageLightbox from '../../components/ImageLightbox';
import Avatar from '../../components/Avatar';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';
import { FLAG_META, fmtDateTime, selectStyles } from './StaffIncidents';

export default function IncidentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);
  const [commentMessage, setCommentMessage] = useState('');
  const [onBehalfOf, setOnBehalfOf] = useState(null);
  const [commentImages, setCommentImages] = useState([]);
  const [addingMention, setAddingMention] = useState(null);
  const [addingBatch, setAddingBatch] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const load = (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    axiosInstance.get(`/admin/incidents/${id}`)
      .then((resp) => setIncident(resp.data?.incident || null))
      .catch(() => { if (!silent) setIncident(null); })
      .finally(() => { setLoading(false); setRefreshing(false); });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    axiosInstance.get('/admin/staff').then((resp) => {
      const staff = resp.data?.staff || [];
      setStaffOptions(staff.map((s) => ({ value: s.staffId, label: s.name })));
    }).catch(() => setStaffOptions([]));

    axiosInstance.get('/admin/batches', { headers: { 'x-accessible-courses': 'all' } }).then((resp) => {
      const batches = resp.data?.batches || [];
      setBatchOptions(batches.map((b) => ({
        value: b.batchId,
        label: b.whatsappGroupName ? `${b.batchName} — ${b.whatsappGroupName}` : b.batchName,
      })));
    }).catch(() => setBatchOptions([]));
  }, []);

  const toggleFlag = async () => {
    if (!incident) return;
    const nextFlag = incident.flag === 'red' ? 'green' : 'red';
    setBusy(true);
    try {
      await axiosInstance.patch(`/admin/incidents/${id}/flag`, { flag: nextFlag });
      load(true);
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to update flag.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const addComment = async () => {
    if (!commentMessage.trim()) return;
    setBusy(true);
    try {
      const resp = await axiosInstance.post(`/admin/incidents/${id}/comments`, {
        message: commentMessage.trim(),
        onBehalfOfStaffId: onBehalfOf?.value || null,
      });
      const commentId = resp.data?.commentId;
      if (commentId) {
        for (const img of commentImages) {
          const form = new FormData();
          form.append('image', img.file);
          await axiosInstance.post(`/admin/incidents/${id}/comments/${commentId}/images`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
      }
      setCommentMessage('');
      setOnBehalfOf(null);
      setCommentImages([]);
      load(true);
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to add comment.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const removeMention = async (staffId) => {
    setBusy(true);
    try {
      await axiosInstance.delete(`/admin/incidents/${id}/mentions/${staffId}`);
      load(true);
    } finally {
      setBusy(false);
    }
  };

  const addMention = async () => {
    if (!addingMention) return;
    setBusy(true);
    try {
      await axiosInstance.post(`/admin/incidents/${id}/mentions`, { staffId: addingMention.value });
      setAddingMention(null);
      load(true);
    } finally {
      setBusy(false);
    }
  };

  const removeBatch = async (batchId) => {
    setBusy(true);
    try {
      await axiosInstance.delete(`/admin/incidents/${id}/batches/${batchId}`);
      load(true);
    } finally {
      setBusy(false);
    }
  };

  const addBatch = async () => {
    if (!addingBatch) return;
    setBusy(true);
    try {
      await axiosInstance.post(`/admin/incidents/${id}/batches`, { batchId: addingBatch.value });
      setAddingBatch(null);
      load(true);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-slate-400">Loading…</div>;
  }
  if (!incident) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 text-sm text-slate-500">
        <p>Incident not found.</p>
        <button onClick={() => navigate('/admin/incidents')} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white">Back to Staff Incidents</button>
      </div>
    );
  }

  const mentionedIds = new Set(incident.mentions.map((m) => m.staffId));
  const availableStaffOptions = staffOptions.filter((s) => !mentionedIds.has(s.value));
  const linkedBatchIds = new Set(incident.batches.map((b) => b.batchId));
  const availableBatchOptions = batchOptions.filter((b) => !linkedBatchIds.has(b.value));

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-6 py-3 shadow-sm">
        <button onClick={() => navigate('/admin/incidents')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
          Back to Staff Incidents
        </button>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          title="Refetch this incident's data"
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
        >
          <svg className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
          </svg>
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="mx-auto w-full max-w-4xl px-6 py-6">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-8 py-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl font-bold text-slate-900">{incident.title}</h1>
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${FLAG_META[incident.flag].badge}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${FLAG_META[incident.flag].dot}`} />
                    {FLAG_META[incident.flag].label}
                  </span>
                </div>
                <p className="mt-1 text-sm text-slate-500">
                  {incident.courseShortName || incident.courseName || 'No course'} · Raised {fmtDateTime(incident.createdAt)}
                  {incident.createdByName ? ` by ${incident.createdByName}` : ''}
                </p>
              </div>
              <button
                onClick={toggleFlag}
                disabled={busy}
                className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${
                  incident.flag === 'red' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-red-100 text-red-700 hover:bg-red-200'
                }`}
              >
                Mark as {incident.flag === 'red' ? 'Green Flag' : 'Red Flag'}
              </button>
            </div>
          </div>

          <div className="px-8 py-6">
            {incident.description && <p className="whitespace-pre-wrap text-sm text-slate-700">{incident.description}</p>}

            {incident.images.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-3">
                {incident.images.map((img, idx) => (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => setLightbox({ images: incident.images, index: idx })}
                    className="block"
                  >
                    <img src={getImageURL(img.imagePath)} alt="" className="h-28 w-28 rounded-xl border border-slate-200 object-cover transition hover:opacity-80" />
                  </button>
                ))}
              </div>
            )}

            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Mentioned Staff</p>
                <div className="flex flex-wrap items-center gap-2">
                  {incident.mentions.map((m) => (
                    <span key={m.staffId} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 py-1 pl-1 pr-2 text-xs font-medium text-slate-700">
                      <Avatar name={m.name} imagePath={m.imagePath} size="h-5 w-5" />
                      {m.name}
                      <button onClick={() => removeMention(m.staffId)} className="text-slate-400 hover:text-red-600" aria-label={`Remove ${m.name}`}>
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </span>
                  ))}
                  {incident.mentions.length === 0 && <p className="text-sm text-slate-400">No staff mentioned yet.</p>}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1">
                    <Select value={addingMention} onChange={setAddingMention} options={availableStaffOptions} placeholder="Add staff…" styles={selectStyles} />
                  </div>
                  <button onClick={addMention} disabled={!addingMention || busy} className="rounded-lg bg-indigo-100 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-200 disabled:opacity-50">
                    Add
                  </button>
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Relevant Batch(es)</p>
                <div className="flex flex-wrap items-center gap-2">
                  {incident.batches.map((b) => (
                    <span key={b.batchId} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 py-1 pl-2.5 pr-2 text-xs font-medium text-slate-700">
                      <span>
                        {b.batchName}
                        {b.whatsappGroupName && <span className="text-slate-400"> · {b.whatsappGroupName}</span>}
                      </span>
                      <button onClick={() => removeBatch(b.batchId)} className="text-slate-400 hover:text-red-600" aria-label={`Remove ${b.batchName}`}>
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </span>
                  ))}
                  {incident.batches.length === 0 && <p className="text-sm text-slate-400">No batches linked yet.</p>}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1">
                    <Select value={addingBatch} onChange={setAddingBatch} options={availableBatchOptions} placeholder="Add a batch…" styles={selectStyles} />
                  </div>
                  <button onClick={addBatch} disabled={!addingBatch || busy} className="rounded-lg bg-indigo-100 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-200 disabled:opacity-50">
                    Add
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-8 border-t border-slate-200 pt-6">
              <p className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-400">Comments ({incident.comments.length}) · oldest first</p>
              <ul className="space-y-4">
                {incident.comments.map((c) => (
                  <li key={c.id} className="flex gap-3">
                    <Avatar name={c.onBehalfOfName || c.createdByName} imagePath={c.onBehalfOfImagePath || c.createdByImagePath} size="h-9 w-9" />
                    <div className="min-w-0 flex-1 rounded-xl border border-slate-100 bg-slate-50 p-3.5">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="text-sm font-semibold text-slate-800">
                          {c.onBehalfOfName || c.createdByName || 'Super Admin'}
                          {c.onBehalfOfName && <span className="ml-1.5 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700">recorded on their behalf</span>}
                        </span>
                        <span className="shrink-0 text-[11px] text-slate-400">{fmtDateTime(c.createdAt)}</span>
                      </div>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{c.message}</p>
                      {c.images.length > 0 && (
                        <div className="mt-2.5 flex flex-wrap gap-2">
                          {c.images.map((img, idx) => (
                            <button
                              key={img.id}
                              type="button"
                              onClick={() => setLightbox({ images: c.images, index: idx })}
                              className="block"
                            >
                              <img src={getImageURL(img.imagePath)} alt="" className="h-24 w-24 rounded-lg border border-slate-200 object-cover transition hover:opacity-80" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
                {incident.comments.length === 0 && <p className="text-sm text-slate-400">No comments yet.</p>}
              </ul>

              <div className="mt-4 space-y-2 rounded-xl border border-slate-200 p-4">
                <textarea
                  value={commentMessage}
                  onChange={(e) => {
                    setCommentMessage(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = `${e.target.scrollHeight}px`;
                  }}
                  rows={2}
                  placeholder="Add a comment… (paragraphs are kept as you paste them)"
                  className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                  style={{ minHeight: '3.5rem', maxHeight: '20rem', overflowY: 'auto' }}
                />
                <div className="sm:w-64">
                  <Select
                    value={onBehalfOf}
                    onChange={setOnBehalfOf}
                    options={staffOptions}
                    isClearable
                    placeholder="Recording on behalf of…"
                    styles={selectStyles}
                  />
                </div>
                <MultiImagePicker images={commentImages} onChange={setCommentImages} label="Attach proof (optional)" />
                <div className="flex justify-end">
                  <button onClick={addComment} disabled={busy || !commentMessage.trim()} className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
                    Post Comment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onNavigate={(index) => setLightbox((prev) => ({ ...prev, index }))}
        />
      )}
    </div>
  );
}
